import { useEffect, useMemo, useRef, useState } from 'react';
import { Wand2, AlertTriangle, CheckCircle2, ChevronDown } from 'lucide-react';
import { t } from '@/i18n/translations';
import { Button } from '@/components/ui/button';
import { toast } from '@/components/ui/sonner';
import {
  buildSuggestions,
  suggestionsKey,
  SUGGESTION_CONSTANTS,
} from '@/lib/quotationSuggestions';

/**
 * Painel "Pré-preencher cotação" — aparece junto ao painel de info detectada
 * (G1) quando há sugestões accionáveis a partir do desenho 2D.
 *
 * UX:
 *  - Sugestões com confidence ≥ 0.85 e campo vazio → auto-aplicadas em
 *    silêncio (toast a confirmar) na primeira render após extracção.
 *  - Sugestões com confidence < 0.85 OU com warning → aparecem como
 *    "pendentes" com checkboxes; user aplica em batch.
 *  - Sugestões cuja célula já tem valor preenchido → mostradas em modo
 *    "ignoradas" (collapsed por defeito) com botão "aplicar mesmo assim".
 *
 * Props:
 *   - extraction: objecto devolvido pelo GET /extraction (drawing 2D)
 *   - alloys: lista de ligas configuradas
 *   - rfq, quotation: estado actual (para detectar campos já preenchidos)
 *   - canEdit: bool — se false, painel não é mostrado
 *   - onApply: (Array<Suggestion>) => void — chamado quando user aplica
 *   - onMarkSuggested: (Array<string>) => void — paths aplicados (para badge UI)
 *   - lang: 'pt'|'en'|'fr'
 */
export default function QuotationSuggestionsPanel({
  extraction,
  alloys,
  rfq,
  quotation,
  canEdit,
  onApply,
  onMarkSuggested,
  lang = 'pt',
}) {
  const [open, setOpen] = useState(true);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [showAlreadyFilled, setShowAlreadyFilled] = useState(false);
  const lastAutoAppliedKey = useRef(null);

  const suggestions = useMemo(
    () => buildSuggestions({ extraction, alloys, rfq, quotation }),
    [extraction, alloys, rfq, quotation],
  );

  const auto = suggestions.filter((s) => s.autoApply);
  const pending = suggestions.filter(
    (s) => !s.autoApply && !s.alreadyHasValue && !s.infoOnly,
  );
  const informational = suggestions.filter((s) => s.infoOnly);
  const alreadyFilled = suggestions.filter((s) => s.alreadyHasValue);

  const extractionKey = suggestionsKey(extraction);

  useEffect(() => {
    if (!canEdit) return;
    if (!extractionKey) return;
    if (lastAutoAppliedKey.current === extractionKey) return;
    if (auto.length === 0) {
      lastAutoAppliedKey.current = extractionKey;
      return;
    }
    onApply?.(auto);
    onMarkSuggested?.(auto.map((s) => s.path));
    toast.success(
      t('suggest_auto_applied', lang).replace('{n}', String(auto.length)),
    );
    lastAutoAppliedKey.current = extractionKey;
  }, [extractionKey, auto, canEdit, onApply, onMarkSuggested, lang]);

  useEffect(() => {
    setSelectedIds(new Set(pending.map((s) => s.id)));
  }, [extractionKey, pending.length]);

  if (!canEdit) return null;
  if (suggestions.length === 0) return null;

  const toggleSelected = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleApplyPending = () => {
    const toApply = pending.filter((s) => selectedIds.has(s.id));
    if (toApply.length === 0) return;
    onApply?.(toApply);
    onMarkSuggested?.(toApply.map((s) => s.path));
    toast.success(
      t('suggest_applied', lang).replace('{n}', String(toApply.length)),
    );
  };

  const handleApplyOverride = (s) => {
    onApply?.([s]);
    onMarkSuggested?.([s.path]);
    toast.success(t('suggest_overridden', lang));
  };

  return (
    <div className="ml-6 rounded-md border border-emerald-500/30 bg-emerald-500/5 p-3 text-xs">
      <button
        type="button"
        className="flex w-full cursor-pointer items-center gap-2 select-none"
        onClick={() => setOpen((v) => !v)}
      >
        <ChevronDown
          className={`h-3.5 w-3.5 transition-transform ${open ? '' : '-rotate-90'}`}
          aria-hidden="true"
        />
        <Wand2 className="h-3.5 w-3.5 text-emerald-600" aria-hidden="true" />
        <span className="font-medium">{t('suggest_panel_title', lang)}</span>
        <span className="text-muted-foreground">
          — {t('suggest_panel_disclaimer', lang)}
        </span>
        <span className="ml-auto rounded bg-emerald-500/15 px-2 py-0.5 text-[10px] font-medium text-emerald-700 dark:text-emerald-300">
          {auto.length + pending.length}{' '}
          {t('suggest_count_label', lang)}
        </span>
      </button>

      {open && (
        <div className="mt-3 space-y-3">
          {auto.length > 0 && (
            <SuggestionsBlock
              title={t('suggest_auto_section', lang)}
              icon={<CheckCircle2 className="h-3 w-3 text-emerald-600" />}
              suggestions={auto}
              variant="applied"
              lang={lang}
            />
          )}

          {pending.length > 0 && (
            <div>
              <div className="mb-1 flex items-center gap-2 text-[11px] font-semibold uppercase tracking-wider text-amber-700 dark:text-amber-300">
                <AlertTriangle className="h-3 w-3" />
                {t('suggest_pending_section', lang)}
              </div>
              <div className="space-y-1">
                {pending.map((s) => (
                  <PendingRow
                    key={s.id}
                    suggestion={s}
                    selected={selectedIds.has(s.id)}
                    onToggle={() => toggleSelected(s.id)}
                    lang={lang}
                  />
                ))}
              </div>
              <div className="mt-2 flex items-center gap-2">
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  onClick={handleApplyPending}
                  disabled={selectedIds.size === 0}
                >
                  <Wand2 className="mr-1 h-3 w-3" />
                  {t('suggest_apply_selected', lang).replace(
                    '{n}',
                    String(selectedIds.size),
                  )}
                </Button>
                <span className="text-[10px] text-muted-foreground">
                  {t('suggest_threshold_hint', lang).replace(
                    '{pct}',
                    String(Math.round(SUGGESTION_CONSTANTS.AUTO_APPLY_THRESHOLD * 100)),
                  )}
                </span>
              </div>
            </div>
          )}

          {informational.length > 0 && (
            <div>
              <div className="mb-1 flex items-center gap-2 text-[11px] font-semibold uppercase tracking-wider text-blue-700 dark:text-blue-300">
                <AlertTriangle className="h-3 w-3" />
                {t('suggest_info_section', lang)}
              </div>
              <div className="space-y-1">
                {informational.map((s) => (
                  <InfoRow key={s.id} suggestion={s} lang={lang} />
                ))}
              </div>
            </div>
          )}

          {alreadyFilled.length > 0 && (
            <details
              open={showAlreadyFilled}
              onToggle={(e) => setShowAlreadyFilled(e.target.open)}
            >
              <summary className="cursor-pointer text-[11px] font-medium text-muted-foreground hover:text-foreground">
                {t('suggest_already_filled_section', lang).replace(
                  '{n}',
                  String(alreadyFilled.length),
                )}
              </summary>
              <div className="mt-2 space-y-1">
                {alreadyFilled.map((s) => (
                  <AlreadyFilledRow
                    key={s.id}
                    suggestion={s}
                    onOverride={() => handleApplyOverride(s)}
                    lang={lang}
                  />
                ))}
              </div>
            </details>
          )}
        </div>
      )}
    </div>
  );
}

function SuggestionsBlock({ title, icon, suggestions, variant, lang }) {
  return (
    <div>
      <div className="mb-1 flex items-center gap-2 text-[11px] font-semibold uppercase tracking-wider text-emerald-700 dark:text-emerald-300">
        {icon}
        {title}
      </div>
      <div className="space-y-1">
        {suggestions.map((s) => (
          <AppliedRow key={s.id} suggestion={s} variant={variant} lang={lang} />
        ))}
      </div>
    </div>
  );
}

function ConfidenceBadge({ confidence }) {
  const pct = Math.round((confidence ?? 0) * 100);
  let cls = 'bg-muted text-muted-foreground';
  if (pct >= 85) cls = 'bg-emerald-500/15 text-emerald-700 dark:text-emerald-300';
  else if (pct >= 70) cls = 'bg-amber-500/15 text-amber-700 dark:text-amber-300';
  else cls = 'bg-orange-500/15 text-orange-700 dark:text-orange-300';
  return (
    <span className={`rounded px-1.5 py-0.5 text-[10px] font-medium ${cls}`}>
      {pct}%
    </span>
  );
}

function AppliedRow({ suggestion, lang }) {
  const s = suggestion;
  return (
    <div className="flex items-center gap-2 rounded border border-emerald-500/20 bg-background/40 px-2 py-1">
      <CheckCircle2 className="h-3 w-3 shrink-0 text-emerald-600" />
      <span className="font-medium text-foreground">{t(s.labelKey, lang)}</span>
      <span className="text-muted-foreground">→</span>
      <span className="font-mono tnum tabular-nums">{s.displayValue}</span>
      <ConfidenceBadge confidence={s.confidence} />
    </div>
  );
}

function PendingRow({ suggestion, selected, onToggle, lang }) {
  const s = suggestion;
  return (
    <label className="flex cursor-pointer items-center gap-2 rounded border border-amber-500/20 bg-background/40 px-2 py-1 hover:bg-background/70">
      <input
        type="checkbox"
        checked={selected}
        onChange={onToggle}
        className="h-3.5 w-3.5"
      />
      <span className="font-medium text-foreground">{t(s.labelKey, lang)}</span>
      <span className="text-muted-foreground">→</span>
      <span className="font-mono tnum tabular-nums">{s.displayValue}</span>
      <ConfidenceBadge confidence={s.confidence} />
      {s.warning && (
        <span className="ml-auto flex items-center gap-1 text-[10px] text-amber-700 dark:text-amber-300">
          <AlertTriangle className="h-3 w-3" />
          {t(s.warning, lang)}
        </span>
      )}
    </label>
  );
}

function InfoRow({ suggestion, lang }) {
  const s = suggestion;
  return (
    <div className="flex items-center gap-2 rounded border border-blue-500/20 bg-background/40 px-2 py-1">
      <AlertTriangle className="h-3 w-3 shrink-0 text-blue-600" />
      <span className="font-medium text-foreground">{t(s.labelKey, lang)}</span>
      <span className="text-muted-foreground">→</span>
      <span className="font-mono tnum tabular-nums">{s.displayValue}</span>
      {s.warning && (
        <span className="ml-auto text-[10px] text-blue-700 dark:text-blue-300">
          {t(s.warning, lang)}
        </span>
      )}
    </div>
  );
}

function AlreadyFilledRow({ suggestion, onOverride, lang }) {
  const s = suggestion;
  return (
    <div className="flex items-center gap-2 rounded border border-border/50 bg-background/40 px-2 py-1 text-muted-foreground">
      <span className="font-medium">{t(s.labelKey, lang)}</span>
      <span>→</span>
      <span className="font-mono tnum tabular-nums">{s.displayValue}</span>
      <ConfidenceBadge confidence={s.confidence} />
      <button
        type="button"
        className="ml-auto text-[10px] underline hover:text-foreground"
        onClick={onOverride}
      >
        {t('suggest_override', lang)}
      </button>
    </div>
  );
}
